import React, { useMemo } from 'react';
import { ListChecks, AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import {
  TASK_STATUS_HEX_COLORS,
  PRIORITY_HEX_COLORS,
  PRIORITY_LABELS,
} from '../../constants/eventStyles';
import type { TaskStatistics, SiteTask } from '../../redux/api/tasksApi';

interface TasksStatsPanelProps {
  taskStats: TaskStatistics | undefined;
  tasks: SiteTask[];
  isLoading: boolean;
}

const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    color: 'var(--color-text-primary)',
    fontSize: '12px',
  },
  itemStyle: { color: 'var(--color-text-primary)' },
  labelStyle: { color: 'var(--color-text-primary)' },
};

const TasksStatsPanel: React.FC<TasksStatsPanelProps> = ({ taskStats, tasks, isLoading }) => {
  const byStatus = useMemo(() => {
    if (taskStats?.by_status) return taskStats.by_status;
    const map: Record<string, number> = {};
    tasks.forEach((t) => { map[t.status] = (map[t.status] || 0) + 1; });
    return map;
  }, [taskStats, tasks]);

  const byPriority = useMemo(() => {
    if (taskStats?.by_priority) return taskStats.by_priority;
    const map: Record<string, number> = {};
    tasks.forEach((t) => { map[String(t.priority)] = (map[String(t.priority)] || 0) + 1; });
    return map;
  }, [taskStats, tasks]);

  const total = taskStats?.total ?? tasks.length;
  const overdue = taskStats?.overdue ?? 0;
  const active = total - (byStatus['completed'] || 0) - (byStatus['cancelled'] || 0);

  const statusPieData = useMemo(() => {
    return Object.entries(byStatus)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        name: status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value: count,
        color: TASK_STATUS_HEX_COLORS[status] || '#9CA3AF',
      }));
  }, [byStatus]);

  const priorityBarData = useMemo(() => {
    return Object.entries(byPriority)
      .filter(([, count]) => count > 0)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([priority, count]) => ({
        name: PRIORITY_LABELS[priority] || `P${priority}`,
        value: count,
        color: PRIORITY_HEX_COLORS[priority] || '#9CA3AF',
      }));
  }, [byPriority]);

  if (isLoading && !taskStats && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 gap-2 text-[var(--color-text-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs">Loading statistics...</span>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="text-center py-4 text-xs text-[var(--color-text-muted)]">
        No task data to display
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          icon={<ListChecks className="w-4 h-4 text-[#17c480]" />}
          label="Total Tasks"
          value={total}
        />
        <MetricCard
          icon={<AlertTriangle className="w-4 h-4 text-red-400" />}
          label="Overdue"
          value={overdue}
          highlight={overdue > 0}
        />
        <MetricCard
          icon={<Clock className="w-4 h-4 text-blue-400" />}
          label="Active"
          value={active}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {statusPieData.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
            <h3 className="text-xs font-semibold text-[var(--color-text-primary)] mb-2">By Status</h3>
            <div className="flex items-center">
              <div className="w-24 h-24 min-w-[96px] min-h-[96px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={42}
                    >
                      {statusPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip {...TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1 ml-2">
                {statusPieData.map((item) => (
                  <LegendItem key={item.name} color={item.color} label={item.name} count={item.value} />
                ))}
              </div>
            </div>
          </div>
        )}

        {priorityBarData.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
            <h3 className="text-xs font-semibold text-[var(--color-text-primary)] mb-2">By Priority</h3>
            <div className="h-24 min-h-[96px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={priorityBarData}
                  layout="vertical"
                  margin={{ top: 0, right: 5, bottom: 0, left: 5 }}
                >
                  <XAxis type="number" tick={{ fontSize: 9, fill: 'var(--color-text-secondary)' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 9, fill: 'var(--color-text-secondary)' }}
                    width={65}
                  />
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    cursor={{ fill: 'var(--color-surface-hover)', opacity: 0.5 }}
                  />
                  <Bar dataKey="value" name="Tasks" activeBar={false} style={{ cursor: 'default' }}>
                    {priorityBarData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function MetricCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`bg-[var(--color-surface)] border rounded-lg p-2.5 flex items-center gap-2.5 ${highlight ? 'border-red-400/40' : 'border-[var(--color-border)]'}`}>
      <div className="p-1.5 bg-[var(--color-surface-hover)] rounded-md">{icon}</div>
      <div>
        <p className="text-[10px] text-[var(--color-text-secondary)] leading-tight">{label}</p>
        <p className="text-sm font-bold text-[var(--color-text-primary)]">{value}</p>
      </div>
    </div>
  );
}

function LegendItem({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
      <span className="text-[var(--color-text-secondary)] flex-1">{label}</span>
      <span className="font-medium text-[var(--color-text-primary)]">{count}</span>
    </div>
  );
}

export default TasksStatsPanel;
